import React, { useState, useEffect } from 'react';
import {
    useDataProvider,
    useTranslate,
    useNotify,
    useRefresh,
} from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Autocomplete,
    CircularProgress,
    Alert,
    Checkbox,
    Divider,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Bolt as BoltIcon,
    Person as PersonIcon,
    Build as BuildIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { apiClient } from '../../utils/apiClient';
import { NumberField } from '../common/NumberField';

interface ManualStartDialogProps {
    open: boolean;
    onClose: () => void;
}

interface Site {
    id: string;
    name: string;
}

interface Connector {
    connectorId: number;
    status: string;
}

interface Charger {
    id: string;
    name: string;
    stationId?: string;
    station_id?: string;  // Backend may return snake_case
    connectorCount: number;
    status: string;
    connectors?: Connector[];  // Connector status information
}

interface AppUser {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    authId: string;
    // Phone is optional in the legacy /app-users response but always
    // present in the lookup-by-phone response. We use it for the
    // confirm-the-name UX in the vendor flow.
    phone?: string | null;
    name?: string;
}

// Country codes for the vendor phone-lookup flow. HK + Macau only — that's
// the supported service area today.
const COUNTRY_CODES: { code: string; label: string }[] = [
    { code: '+852', label: '🇭🇰 +852' },
    { code: '+853', label: '🇲🇴 +853' },
];

type SessionMode = 'on-behalf' | 'standalone';

export const ManualStartDialog: React.FC<ManualStartDialogProps> = ({
    open,
    onClose,
}) => {
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();

    // Form state
    const [mode, setMode] = useState<SessionMode>('on-behalf');
    const [selectedSite, setSelectedSite] = useState<Site | null>(null);
    const [selectedCharger, setSelectedCharger] = useState<Charger | null>(null);
    const [selectedConnector, setSelectedConnector] = useState<number | null>(1);
    const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
    const [estimatedKwh, setEstimatedKwh] = useState<number | null>(10.0);
    const [createPreAuth, setCreatePreAuth] = useState<boolean>(true);
    const [adminNotes, setAdminNotes] = useState<string>('');

    // Phone-lookup state for the on-behalf flow. The previous free-text search
    // dropdown is gone — every admin (super or vendor-scoped) now identifies
    // the customer by an exact phone-number match.
    const [phoneCountryCode, setPhoneCountryCode] = useState<string>('+852');
    const [phoneLocal, setPhoneLocal] = useState<string>('');
    const [phoneLookupLoading, setPhoneLookupLoading] = useState<boolean>(false);
    const [phoneLookupNotFound, setPhoneLookupNotFound] = useState<boolean>(false);

    // Loading states
    const [sitesLoading, setSitesLoading] = useState(false);
    const [chargersLoading, setChargersLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Data lists
    const [sites, setSites] = useState<Site[]>([]);
    const [chargers, setChargers] = useState<Charger[]>([]);

    // Error state
    const [error, setError] = useState<string>('');

    // Load sites when dialog opens
    useEffect(() => {
        if (open) {
            loadSites();
            resetForm();
        }
    }, [open]);

    // Load chargers when site changes
    useEffect(() => {
        if (selectedSite) {
            loadChargers(selectedSite.id);
        } else {
            setChargers([]);
            setSelectedCharger(null);
        }
    }, [selectedSite]);

    const loadSites = async () => {
        setSitesLoading(true);
        try {
            const { data } = await dataProvider.getList('sites', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {},
            });
            setSites(data);
        } catch (error) {
            notify(translate('resources.active-sessions.manualStart.errorLoadSites'), { type: 'error' });
        } finally {
            setSitesLoading(false);
        }
    };

    const loadChargers = async (siteId: string) => {
        setChargersLoading(true);
        try {
            const { data } = await dataProvider.getList('chargers', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: { siteId },
            });
            // Filter to only available chargers
            const availableChargers = data.filter((c: Charger) => c.status === 'available');
            setChargers(availableChargers);

            if (availableChargers.length === 0) {
                notify(translate('resources.active-sessions.manualStart.warningNoChargers'), { type: 'warning' });
            }
        } catch (error) {
            notify(translate('resources.active-sessions.manualStart.errorLoadChargers'), { type: 'error' });
        } finally {
            setChargersLoading(false);
        }
    };

    const resetForm = () => {
        setMode('on-behalf');
        setSelectedSite(null);
        setSelectedCharger(null);
        setSelectedConnector(1);
        setSelectedUser(null);
        setEstimatedKwh(10.0);
        setCreatePreAuth(true);
        setAdminNotes('');
        setError('');
        setPhoneCountryCode('+852');
        setPhoneLocal('');
        setPhoneLookupNotFound(false);
    };

    const lookupUserByPhone = async () => {
        if (!phoneLocal) return;
        const fullPhone = `${phoneCountryCode}${phoneLocal.replace(/\D/g, '')}`;
        setPhoneLookupLoading(true);
        setPhoneLookupNotFound(false);
        setSelectedUser(null);
        try {
            const result = await apiClient.get<{ data: any }>(
                `/api/backend/app-users/lookup-by-phone?phone=${encodeURIComponent(fullPhone)}`,
            );
            const u = result.data;
            // Backend's formatAppUserMinimal returns { id, name, email, phone, ... }
            // which doesn't quite match the AppUser shape used by the rest of this
            // form. Fold both shapes together — name takes priority, otherwise we
            // fall back to first/last name from a regular /app-users response.
            setSelectedUser({
                id: u.id,
                authId: u.authId || '',
                email: u.email || '',
                firstName: u.firstName,
                lastName: u.lastName,
                name: u.name,
                phone: u.phone || fullPhone,
            });
        } catch (error: any) {
            // 404 from backend → genuine "no user with this phone"
            if (error?.status === 404 || error?.errorCode === 'APP_USER_NOT_FOUND') {
                setPhoneLookupNotFound(true);
            } else {
                notify(
                    error?.message || translate('resources.active-sessions.manualStart.errorSearchUsers'),
                    { type: 'error' },
                );
            }
        } finally {
            setPhoneLookupLoading(false);
        }
    };

    const validateForm = (): boolean => {
        if (!selectedSite) {
            setError(translate('resources.active-sessions.manualStart.errorSelectSite'));
            return false;
        }
        if (!selectedCharger) {
            setError(translate('resources.active-sessions.manualStart.errorSelectCharger'));
            return false;
        }
        if (mode === 'on-behalf' && !selectedUser) {
            setError(translate('resources.active-sessions.manualStart.errorSelectUser'));
            return false;
        }
        // Admin notes are optional - no validation needed
        if (selectedConnector === null || selectedConnector < 1) {
            setError(translate('resources.active-sessions.manualStart.errorInvalidConnector', { _: 'Please select a valid connector' }));
            return false;
        }
        if (estimatedKwh === null || estimatedKwh <= 0) {
            setError(translate('resources.active-sessions.manualStart.errorInvalidKwh'));
            return false;
        }
        setError('');
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const trimmedNotes = adminNotes.trim();
            if (mode === 'on-behalf' && selectedUser) {
                // Start on-behalf session
                const payload: Record<string, unknown> = {
                    app_user_id: selectedUser.id,
                    charger_id: selectedCharger!.id,
                    connector_id: selectedConnector,
                    create_pre_auth: createPreAuth,
                    estimated_kwh: estimatedKwh,
                };
                if (trimmedNotes) payload.admin_notes = trimmedNotes;
                const result = await apiClient.post('/api/backend/charging/start-on-behalf', payload);

                notify(translate('resources.active-sessions.manualStart.successOnBehalf'), { type: 'success' });
                refresh();
                handleClose();
            } else {
                // Start standalone session
                const payload: Record<string, unknown> = {
                    charger_id: selectedCharger!.id,
                    connector_id: selectedConnector,
                    estimated_kwh: estimatedKwh,
                };
                if (trimmedNotes) payload.admin_notes = trimmedNotes;
                const result = await apiClient.post('/api/backend/charging/start-standalone', payload);

                notify(translate('resources.active-sessions.manualStart.successStandalone'), { type: 'success' });
                refresh();
                handleClose();
            }
        } catch (error: any) {
            // Use error code for translation if available, otherwise use message
            let errorMessage: string;
            if (error.errorCode) {
                // Try to translate the error code
                const translatedMessage = translate(`errors.${error.errorCode}`, { _: error.message });
                errorMessage = translatedMessage !== `errors.${error.errorCode}` ? translatedMessage : error.message;
            } else {
                errorMessage = error.message || translate('resources.active-sessions.manualStart.errorStartSession');
            }
            setError(errorMessage);
            notify(errorMessage, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            resetForm();
            onClose();
        }
    };

    const getConnectorOptions = (): number[] => {
        if (!selectedCharger) return [1];
        return Array.from({ length: selectedCharger.connectorCount }, (_, i) => i + 1);
    };

    const getChargerDisplayLabel = (charger: Charger): string => {
        const stationId = charger.stationId || charger.station_id;
        const baseName = stationId ? `${charger.name} (${stationId})` : charger.name;

        // Add connector status if available
        if (charger.connectors && charger.connectors.length > 0) {
            const connectorStatuses = charger.connectors
                .map(c => `C${c.connectorId}:${c.status}`)
                .join(', ');
            return `${baseName} [${connectorStatuses}]`;
        }

        return baseName;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown={submitting}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BoltIcon sx={{ color: '#00C853' }} />
                    <Typography variant="h6">{translate('resources.active-sessions.manualStart.title')}</Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    {/* Session Mode Selection */}
                    <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                            {translate('resources.active-sessions.manualStart.sessionMode')}
                        </FormLabel>
                        <RadioGroup
                            row
                            value={mode}
                            onChange={(e) => setMode(e.target.value as SessionMode)}
                        >
                            <FormControlLabel
                                value="on-behalf"
                                control={<Radio />}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PersonIcon fontSize="small" />
                                        <span>{translate('resources.active-sessions.manualStart.onBehalf')}</span>
                                    </Box>
                                }
                            />
                            <FormControlLabel
                                value="standalone"
                                control={<Radio />}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <BuildIcon fontSize="small" />
                                        <span>{translate('resources.active-sessions.manualStart.standalone')}</span>
                                    </Box>
                                }
                            />
                        </RadioGroup>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            {mode === 'on-behalf'
                                ? translate('resources.active-sessions.manualStart.onBehalfDescription')
                                : translate('resources.active-sessions.manualStart.standaloneDescription')}
                        </Typography>
                    </FormControl>

                    <Divider />

                    {/* Customer identification (On-Behalf mode only): exact phone-number
                        lookup. Replaces the old free-text search dropdown — every admin
                        identifies the customer by phone now. The lookup endpoint enforces
                        exact match, returns 404 on no match, and audit-logs each attempt. */}
                    {mode === 'on-behalf' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <FormLabel sx={{ fontWeight: 600 }}>
                                {translate('resources.active-sessions.manualStart.lookupByPhone', {
                                    _: 'Look up customer by phone number',
                                })}{' '}*
                            </FormLabel>
                            {/*
                                Match the Select + TextField to the Button's natural
                                height (MUI default medium Button = 36.5px). The Button
                                itself is left untouched; the inputs are pinned via
                                MuiInputBase-root so they line up on the same centerline.
                            */}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Select
                                    value={phoneCountryCode}
                                    onChange={(e) => setPhoneCountryCode(e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 130, height: 46.5 }}
                                >
                                    {COUNTRY_CODES.map((c) => (
                                        <MenuItem key={c.code} value={c.code}>
                                            {c.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <TextField
                                    value={phoneLocal}
                                    onChange={(e) => {
                                        // Digits only; HK and Macau both use 8-digit local numbers.
                                        const v = e.target.value.replace(/\D/g, '').slice(0, 12);
                                        setPhoneLocal(v);
                                        setPhoneLookupNotFound(false);
                                        // Clear any previous match — operator must re-confirm
                                        // each lookup so they can't accidentally start a session
                                        // for a stale match.
                                        setSelectedUser(null);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && phoneLocal && !phoneLookupLoading) {
                                            e.preventDefault();
                                            lookupUserByPhone();
                                        }
                                    }}
                                    placeholder={translate('resources.active-sessions.manualStart.phonePlaceholder', {
                                        _: 'e.g. 12345678',
                                    })}
                                    size="small"
                                    inputMode="numeric"
                                    sx={{
                                        flex: 1,
                                        '& .MuiInputBase-root': { height: 46.5 },
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={lookupUserByPhone}
                                    disabled={!phoneLocal || phoneLookupLoading}
                                    startIcon={phoneLookupLoading
                                        ? <CircularProgress size={16} />
                                        : <SearchIcon />}
                                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                >
                                    {translate('resources.active-sessions.manualStart.lookup', {
                                        _: 'Look up',
                                    })}
                                </Button>
                            </Box>

                            {phoneLookupNotFound && (
                                <Alert severity="warning">
                                    {translate('resources.active-sessions.manualStart.phoneNotFound', {
                                        _: 'No customer found with this phone number',
                                    })}
                                </Alert>
                            )}

                            {selectedUser && (
                                <Alert
                                    severity="info"
                                    icon={<PersonIcon />}
                                    sx={{
                                        '& .MuiAlert-message': { width: '100%' },
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {translate('resources.active-sessions.manualStart.confirmCustomer', {
                                            _: 'Confirm with the customer that this is them:',
                                        })}
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 0.25, mt: 0.5 }}>
                                        <Typography variant="body2">
                                            <strong>
                                                {translate('resources.active-sessions.manualStart.name', { _: 'Name' })}:
                                            </strong>{' '}
                                            {selectedUser.name
                                                || [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(' ')
                                                || '—'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>
                                                {translate('resources.active-sessions.manualStart.email', { _: 'Email' })}:
                                            </strong>{' '}
                                            {selectedUser.email || '—'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>
                                                {translate('resources.active-sessions.manualStart.phone', { _: 'Phone' })}:
                                            </strong>{' '}
                                            {selectedUser.phone || '—'}
                                        </Typography>
                                    </Box>
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* Site Selection */}
                    <Autocomplete
                        options={sites}
                        loading={sitesLoading}
                        value={selectedSite}
                        onChange={(_, newValue) => {
                            setSelectedSite(newValue);
                            setSelectedCharger(null);
                        }}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={`${translate('resources.active-sessions.manualStart.site')} *`}
                                placeholder={translate('resources.active-sessions.manualStart.sitePlaceholder')}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {sitesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Charger Selection */}
                    <Autocomplete
                        options={chargers}
                        loading={chargersLoading}
                        value={selectedCharger}
                        onChange={(_, newValue) => {
                            setSelectedCharger(newValue);
                            setSelectedConnector(1);
                        }}
                        getOptionLabel={getChargerDisplayLabel}
                        renderOption={(props, option) => (
                            <li {...props}>
                                <Box sx={{ width: '100%' }}>
                                    <Typography variant="body2">
                                        {option.name} {option.stationId || option.station_id ? `(${option.stationId || option.station_id})` : ''}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.active-sessions.manualStart.status')}: {option.status}
                                        {option.connectors && option.connectors.length > 0 && (
                                            <> | {translate('resources.active-sessions.manualStart.connectors')}: {option.connectors.map(c => `#${c.connectorId}: ${c.status}`).join(', ')}</>
                                        )}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        disabled={!selectedSite}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={`${translate('resources.active-sessions.manualStart.charger')} *`}
                                placeholder={selectedSite ? translate('resources.active-sessions.manualStart.chargerPlaceholder') : translate('resources.active-sessions.manualStart.chargerPlaceholderNoSite')}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {chargersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Connector Selection */}
                    <Autocomplete
                        options={getConnectorOptions()}
                        value={selectedConnector}
                        onChange={(_, newValue) => setSelectedConnector(newValue ?? null)}
                        getOptionLabel={(option) => {
                            const connector = selectedCharger?.connectors?.find(c => c.connectorId === option);
                            return connector ? `${translate('resources.active-sessions.manualStart.connectorStatus')} ${option} (${connector.status})` : `${translate('resources.active-sessions.manualStart.connectorStatus')} ${option}`;
                        }}
                        renderOption={(props, option) => {
                            const connector = selectedCharger?.connectors?.find(c => c.connectorId === option);
                            return (
                                <li {...props}>
                                    <Box sx={{ width: '100%' }}>
                                        <Typography variant="body2">
                                            {translate('resources.active-sessions.manualStart.connectorStatus')} {option}
                                        </Typography>
                                        {connector && (
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('resources.active-sessions.manualStart.status')}: {connector.status}
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            );
                        }}
                        disabled={!selectedCharger}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={`${translate('resources.active-sessions.manualStart.connector')} *`}
                                placeholder={translate('resources.active-sessions.manualStart.connectorPlaceholder')}
                            />
                        )}
                    />

                    <Divider />

                    {/* Estimated kWh */}
                    <NumberField
                        label={`${translate('resources.active-sessions.manualStart.estimatedKwh')} *`}
                        value={estimatedKwh}
                        onValueChange={(v) => setEstimatedKwh(v ?? null)}
                        inputProps={{ min: 0.1, step: 0.1 }}
                        helperText={translate('resources.active-sessions.manualStart.estimatedKwhHelp')}
                        fullWidth
                    />

                    {/* Pre-Auth Checkbox (On-Behalf mode only) */}
                    {mode === 'on-behalf' && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={createPreAuth}
                                    onChange={(e) => setCreatePreAuth(e.target.checked)}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2">{translate('resources.active-sessions.manualStart.createPreAuth')}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.active-sessions.manualStart.createPreAuthHelp')}
                                    </Typography>
                                </Box>
                            }
                        />
                    )}

                    {/* Admin Notes */}
                    <TextField
                        label={translate('resources.active-sessions.manualStart.adminNotes')}
                        multiline
                        rows={3}
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder={translate('resources.active-sessions.manualStart.adminNotesPlaceholder')}
                        helperText={translate('resources.active-sessions.manualStart.adminNotesHelp')}
                        fullWidth
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} disabled={submitting}>
                    {translate('resources.active-sessions.manualStart.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting || !selectedCharger || (mode === 'on-behalf' && !selectedUser)}
                    startIcon={submitting ? <CircularProgress size={16} /> : <BoltIcon />}
                    sx={{
                        backgroundColor: '#00C853',
                        '&:hover': {
                            backgroundColor: '#00A843',
                        },
                    }}
                >
                    {submitting ? translate('resources.active-sessions.manualStart.starting') : translate('resources.active-sessions.manualStart.startSession')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
